// FILE: src/components/AdminDashboard.js
//
// Admin Dashboard — read-only operational visibility into the question banks.
//
// ── Layout ─────────────────────────────────────────────────────────────────────
//
//   Header (Back, title, Last refreshed, Refresh)
//   Exam tabs  [All | PMP® | CPMAI™]
//   ┌───────────────────────────────────────────────┐
//   │  Summary stat cards (clickable — 8 cards)     │
//   ├───────────────────────────────────────────────┤
//   │  ▼ Filtered Question List  (when card active) │
//   ├───────────────────────────────────────────────┤
//   │  Data quality alerts                          │
//   ├──────────────────────┬────────────────────────┤
//   │  Category breakdown  │  Question types        │
//   ├──────────────────────┴────────────────────────┤
//   │  Recent activity feed                         │
//   └───────────────────────────────────────────────┘

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { loadDashboardData, isMissingExplanation, QUESTION_TYPES, CHANGE_TYPE_META, PASS_THRESHOLD } from '../../services/dashboardService';
import { getAllExams } from '../../config/exams';
import {
  QUESTION_STATUS_LABELS, QUESTION_STATUS_COLORS,
  REVIEW_STATUS_LABELS,   REVIEW_STATUS_COLORS,
} from '../../config/lifecycleConstants';
import {
  EXAM_CATEGORIES,
  PMP_PERFORMANCE_DOMAINS,
  PMP_DELIVERY_CONTEXTS,
} from '../../config/examCategories';
import { computeCoverageMatrix, COVERAGE_BANDS, maxDomainColumns, formatEta } from '../../services/coverageService';
import { getPublishThroughput } from '../../services/questionFactoryService';

// ── Design tokens ─────────────────────────────────────────────────────────────

const BRAND = {
  headerBg:   '#1a1a2e',
  pageBg:     '#f5f5f5',
  cardBg:     '#fff',
  border:     '#e0e0e0',
  text:       '#222',
  textMuted:  '#666',
  textLight:  '#999',
  accent:     '#1565c0',
  fontFamily: "'Segoe UI', system-ui, sans-serif",
};

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  wrapper: {
    fontFamily: BRAND.fontFamily,
    minHeight: '100vh',
    background: BRAND.pageBg,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: BRAND.headerBg,
    color: '#fff',
    padding: '14px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.85rem',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    flex: 1,
    letterSpacing: '0.5px',
  },
  headerMeta: {
    fontSize: '0.75rem',
    color: '#aaa',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  refreshButton: {
    padding: '5px 12px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.78rem',
  },
  body: {
    flex: 1,
    padding: '28px 32px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  tabBar: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    borderBottom: `2px solid ${BRAND.border}`,
    paddingBottom: '0',
  },
  tab: (active) => ({
    padding: '8px 20px',
    background: active ? '#fff' : 'transparent',
    border: active ? `2px solid ${BRAND.border}` : '2px solid transparent',
    borderBottom: active ? '2px solid #fff' : '2px solid transparent',
    borderRadius: '6px 6px 0 0',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: active ? '600' : '400',
    color: active ? BRAND.accent : BRAND.textMuted,
    marginBottom: '-2px',
    transition: 'color 0.1s',
  }),
  sectionLabel: {
    fontSize: '0.75rem',
    fontWeight: '700',
    color: BRAND.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '12px',
    marginTop: '0',
  },

  // ── Stat cards ──────────────────────────────────────────────────────────────
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
  },
  statCard: (accent, active) => ({
    background: active ? accent : BRAND.cardBg,
    border: `2px solid ${active ? accent : BRAND.border}`,
    borderTop: `3px solid ${accent}`,
    borderRadius: '8px',
    padding: '16px 14px 14px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
    boxShadow: active ? `0 2px 8px ${accent}44` : 'none',
    outline: 'none',
  }),
  statValue: (accent, active) => ({
    fontSize: '2rem',
    fontWeight: '700',
    color: active ? '#fff' : accent,
    lineHeight: 1,
    marginBottom: '6px',
  }),
  statLabel: (active) => ({
    fontSize: '0.72rem',
    color: active ? 'rgba(255,255,255,0.85)' : BRAND.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '600',
  }),

  // ── Filtered question list ─────────────────────────────────────────────────
  filterPanel: {
    background: BRAND.cardBg,
    border: `1px solid ${BRAND.border}`,
    borderRadius: '8px',
    marginBottom: '24px',
    overflow: 'hidden',
  },
  filterHeader: {
    padding: '12px 16px',
    background: '#f8f8f8',
    borderBottom: `1px solid ${BRAND.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  filterTitle: {
    fontSize: '0.82rem',
    fontWeight: '600',
    color: BRAND.text,
  },
  filterClear: {
    padding: '3px 10px',
    background: 'transparent',
    border: `1px solid ${BRAND.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    color: BRAND.textMuted,
  },
  filterTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8rem',
  },
  filterTh: {
    padding: '8px 12px',
    textAlign: 'left',
    fontWeight: '600',
    color: BRAND.textMuted,
    borderBottom: `1px solid ${BRAND.border}`,
    background: '#fafafa',
    fontSize: '0.73rem',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  filterTd: {
    padding: '8px 12px',
    borderBottom: '1px solid #f5f5f5',
    verticalAlign: 'middle',
    color: BRAND.text,
  },
  emptyFilter: {
    padding: '24px',
    textAlign: 'center',
    color: BRAND.textLight,
    fontSize: '0.82rem',
  },

  // ── Quality alerts ─────────────────────────────────────────────────────────
  alertGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '10px',
    marginBottom: '28px',
  },
  alertCard: (warn) => ({
    background: warn ? '#fff8e1' : '#f1f8f1',
    border: `1px solid ${warn ? '#ffe082' : '#a5d6a7'}`,
    borderRadius: '8px',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  }),
  alertIcon: {
    fontSize: '1.2rem',
    flexShrink: 0,
    marginTop: '1px',
  },
  alertText: {
    fontSize: '0.82rem',
    color: BRAND.text,
    lineHeight: '1.4',
  },
  alertCount: (warn) => ({
    fontWeight: '700',
    color: warn ? '#e07b00' : '#2e7d32',
  }),

  // ── Two-column lower section ────────────────────────────────────────────────
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '28px',
  },
  card: {
    background: BRAND.cardBg,
    border: `1px solid ${BRAND.border}`,
    borderRadius: '8px',
    padding: '20px',
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
    fontSize: '0.82rem',
  },
  barLabel: {
    width: '175px',
    flexShrink: 0,
    color: BRAND.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  barTrack: {
    flex: 1,
    height: '10px',
    background: '#eee',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  barFill: (pct, color) => ({
    height: '100%',
    width: `${Math.min(pct, 100)}%`,
    background: color,
    borderRadius: '5px',
    transition: 'width 0.3s',
  }),
  barCount: {
    width: '32px',
    textAlign: 'right',
    color: BRAND.textMuted,
    fontSize: '0.78rem',
    flexShrink: 0,
  },

  // ── Activity feed ──────────────────────────────────────────────────────────
  activityCard: {
    background: BRAND.cardBg,
    border: `1px solid ${BRAND.border}`,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '28px',
  },
  activityList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '10px 0',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.83rem',
  },
  activityIcon: {
    fontSize: '1rem',
    flexShrink: 0,
    width: '20px',
    textAlign: 'center',
    marginTop: '2px',
  },
  activityBody: {
    flex: 1,
    color: BRAND.text,
    lineHeight: '1.4',
  },
  activityMeta: {
    color: BRAND.textLight,
    fontSize: '0.75rem',
    marginTop: '2px',
  },
  activityTime: {
    color: BRAND.textLight,
    fontSize: '0.75rem',
    flexShrink: 0,
    marginTop: '2px',
  },

  // ── States ────────────────────────────────────────────────────────────────
  center: {
    textAlign: 'center',
    padding: '60px 20px',
    color: BRAND.textMuted,
    fontSize: '0.9rem',
  },
  errorBox: {
    background: '#ffebee',
    border: '1px solid #ef9a9a',
    borderRadius: '8px',
    padding: '16px',
    color: '#c62828',
    fontSize: '0.85rem',
    marginBottom: '20px',
  },
  badge: (colors) => ({
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: '10px',
    fontSize: '0.7rem',
    fontWeight: '700',
    border: `1px solid ${colors.border}`,
    color: colors.color,
    background: colors.bg,
    whiteSpace: 'nowrap',
  }),
  emptyRow: {
    padding: '16px 0',
    color: BRAND.textLight,
    fontSize: '0.82rem',
    textAlign: 'center',
  },

  // ── Session metrics table ──────────────────────────────────────────────────
  sessionCard: {
    background: BRAND.cardBg,
    border: `1px solid ${BRAND.border}`,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '28px',
    overflowX: 'auto',
  },
  sessionTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.83rem',
  },
  sessionTh: {
    padding: '8px 14px',
    textAlign: 'left',
    fontWeight: '600',
    color: BRAND.textMuted,
    borderBottom: `2px solid ${BRAND.border}`,
    fontSize: '0.73rem',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
  },
  sessionThRight: {
    padding: '8px 14px',
    textAlign: 'right',
    fontWeight: '600',
    color: BRAND.textMuted,
    borderBottom: `2px solid ${BRAND.border}`,
    fontSize: '0.73rem',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
  },
  sessionTd: {
    padding: '10px 14px',
    borderBottom: `1px solid #f5f5f5`,
    color: BRAND.text,
    verticalAlign: 'middle',
  },
  sessionTdRight: {
    padding: '10px 14px',
    borderBottom: `1px solid #f5f5f5`,
    textAlign: 'right',
    fontWeight: '600',
    color: BRAND.text,
    verticalAlign: 'middle',
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRelativeTime(iso) {
  if (!iso) return '';
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins  <  1) return 'just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days  <  7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatTimestamp(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

function truncate(str, max = 80) {
  if (!str) return '—';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

// Quick lookup: question-type key → human label (plus the synthetic 'unknown'
// bucket used for any type not in the known catalogue).
const QUESTION_TYPE_LABELS = {
  ...Object.fromEntries(QUESTION_TYPES.map((t) => [t.key, t.label])),
  unknown: 'Other / Unknown',
};

// ── Filter logic ──────────────────────────────────────────────────────────────

/**
 * Returns the subset of questions that match an active filter card.
 * filter = { field: 'status'|'reviewStatus'|'category', value: string,
 *            validCategories?: string[] }
 *
 * For category filters:
 *   '(uncategorized)' → questions with no category
 *   '(invalid)'       → questions whose category isn't in validCategories
 *   anything else     → exact match on q.category
 */
export function applyFilter(questions, filter) {
  if (!filter) return [];
  return questions.filter((q) => {
    if (filter.field === 'status')       return (q.status      || 'active')      === filter.value;
    if (filter.field === 'reviewStatus') return (q.reviewStatus || 'unreviewed')  === filter.value;

    if (filter.field === 'category') {
      const cat = (q.category || '').trim();
      if (filter.value === '(uncategorized)') return cat === '';
      if (filter.value === '(invalid)') {
        const valid =
          (filter.validCategoriesByExam && q._examId)
            ? (filter.validCategoriesByExam[q._examId] || [])
            : (filter.validCategories || []);
        return cat !== '' && valid.length > 0 && !valid.includes(cat);
      }
      return cat === filter.value;
    }

    if (filter.field === 'type') {
      const t = q.type || 'multiple_choice';
      if (filter.value === 'unknown') {
        return !(Array.isArray(filter.knownTypes) && filter.knownTypes.includes(t));
      }
      return t === filter.value;
    }

    if (filter.field === 'performanceDomain') {
      const pd = (q.performanceDomain || '').trim();
      const valid = filter.validValues || [];
      if (filter.value === '(unassigned)') {
        return pd === '' || (valid.length > 0 && !valid.includes(pd));
      }
      return pd === filter.value;
    }

    if (filter.field === 'deliveryContext') {
      const dc = (q.deliveryContext || '').trim();
      const valid = filter.validValues || [];
      if (filter.value === '(unassigned)') {
        return dc === '' || (valid.length > 0 && !valid.includes(dc));
      }
      return dc === filter.value;
    }

    if (filter.field === 'focusArea') {
      const key = filter.dimensionKey || 'focusArea';
      const raw = (q[key] != null ? String(q[key]).trim() : '');
      const labels = filter.barLabels || [];
      if (filter.value === '(missing)') return raw === '';
      if (filter.value === '(invalid)') return raw !== '' && !labels.includes(raw);
      return raw === filter.value;
    }

    if (filter.field === 'missingExplanation') {
      // Shared with the metric in dashboardService so the card count and the
      // list it opens can never disagree. Handles both the summary projection
      // (hasExplanation boolean) and bundled fallback rows (explanation text).
      return isMissingExplanation(q);
    }

    return false;
  });
}

/**
 * Returns questions for the currently selected exam tab(s).
 * For 'all', returns all questions with an added examId field.
 */
function questionsForTab(allQuestions, selectedExam) {
  if (selectedExam === 'all') {
    return Object.entries(allQuestions).flatMap(([examId, qs]) =>
      qs.map((q) => ({ ...q, _examId: examId }))
    );
  }
  return (allQuestions[selectedExam] || []).map((q) => ({ ...q, _examId: selectedExam }));
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, accent, active, onClick }) {
  return (
    <button
      style={S.statCard(accent, active)}
      onClick={onClick}
      title={active ? `Click to clear filter` : `Click to filter by "${label}"`}
    >
      <div style={S.statValue(accent, active)}>{value}</div>
      <div style={S.statLabel(active)}>{label}</div>
      {active && (
        <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
          ✕ click to clear
        </div>
      )}
    </button>
  );
}

function FilteredQuestionList({ filter, questions, selectedExam, filterPanelRef, onEditQuestion }) {
  if (!filter) return null;

  const filtered = applyFilter(questions, filter);
  const showExam = selectedExam === 'all';

  const phaseOrCategory = selectedExam === 'pmp' ? 'Phase' : 'Category';
  const filterLabel =
    filter.field === 'status'             ? (QUESTION_STATUS_LABELS[filter.value] || filter.value) :
    filter.field === 'reviewStatus'       ? (REVIEW_STATUS_LABELS[filter.value]   || filter.value) :
    filter.field === 'category'           ? `${phaseOrCategory}: ${filter.value}` :
    filter.field === 'type'               ? `Type: ${QUESTION_TYPE_LABELS[filter.value] || filter.value}` :
    filter.field === 'performanceDomain'  ? `Performance Domain: ${filter.value}` :
    filter.field === 'deliveryContext'    ? `Delivery Context: ${filter.value}` :
    filter.field === 'focusArea'          ? `${filter.dimensionLabel || 'Focus Area'}: ${filter.value}` :
    filter.field === 'missingExplanation' ? 'Missing an explanation' :
    filter.value;

  return (
    <div ref={filterPanelRef} style={S.filterPanel}>
      <div style={S.filterHeader}>
        <span style={S.filterTitle}>
          Showing {filtered.length} question{filtered.length !== 1 ? 's' : ''} — {filterLabel}
        </span>
        {onEditQuestion && (
          <span style={{ fontSize: '0.72rem', color: BRAND.textMuted, fontStyle: 'italic' }}>
            Click a Ref ID to open in editor
          </span>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={S.emptyFilter}>No questions match this filter.</div>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto' }}>
          <table style={S.filterTable}>
            <thead>
              <tr>
                {showExam && <th style={S.filterTh}>Exam</th>}
                <th style={S.filterTh}>Ref ID</th>
                <th style={S.filterTh}>Status</th>
                <th style={S.filterTh}>Review</th>
                <th style={S.filterTh}>{selectedExam === 'pmp' ? 'Phase' : 'Category'}</th>
                <th style={S.filterTh}>Question</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => {
                const statusColors = QUESTION_STATUS_COLORS[q.status] || QUESTION_STATUS_COLORS.active;
                const reviewColors = REVIEW_STATUS_COLORS[q.reviewStatus] || REVIEW_STATUS_COLORS.unreviewed;
                const canEdit = !!onEditQuestion && !!q._examId;
                return (
                  <tr key={`${q._examId}-${q.id}`}>
                    {showExam && (
                      <td style={S.filterTd}>
                        <span style={{ fontSize: '0.72rem', fontWeight: '600', color: BRAND.textMuted }}>
                          {q._examId?.toUpperCase()}
                        </span>
                      </td>
                    )}
                    <td style={S.filterTd}>
                      {canEdit ? (
                        <button
                          onClick={() => onEditQuestion(q._examId, q.id)}
                          style={{
                            fontFamily: 'monospace', fontSize: '0.75rem',
                            color: BRAND.accent, background: 'none', border: 'none',
                            padding: 0, cursor: 'pointer', textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                          }}
                          title="Open in Question Editor"
                        >
                          {q.referenceId || q.id}
                        </button>
                      ) : (
                        <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: BRAND.accent }}>
                          {q.referenceId || q.id}
                        </span>
                      )}
                    </td>
                    <td style={S.filterTd}>
                      <span style={S.badge(statusColors)}>
                        {QUESTION_STATUS_LABELS[q.status] || q.status}
                      </span>
                    </td>
                    <td style={S.filterTd}>
                      <span style={S.badge(reviewColors)}>
                        {REVIEW_STATUS_LABELS[q.reviewStatus] || q.reviewStatus}
                      </span>
                    </td>
                    <td style={{ ...S.filterTd, color: BRAND.textMuted, fontSize: '0.78rem' }}>
                      {q.category || <span style={{ color: BRAND.textLight }}>—</span>}
                    </td>
                    <td style={{ ...S.filterTd, maxWidth: '380px' }}>
                      {truncate(q.question, 90)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryCards({ m, activeFilter, onFilterChange }) {
  const statusColors = { new: '#1565c0', active: '#2e7d32', retired: '#777' };
  const reviewColors = { unreviewed: '#777', reported: '#c62828', in_review: '#e07b00', reviewed: '#2e7d32' };

  function toggle(field, value) {
    if (activeFilter?.field === field && activeFilter?.value === value) {
      onFilterChange(null); // deselect
    } else {
      onFilterChange({ field, value });
    }
  }

  const isActive = (field, value) =>
    activeFilter?.field === field && activeFilter?.value === value;

  return (
    <>
      <p style={S.sectionLabel}>Question Bank Health — click a card to filter</p>
      <div style={S.statGrid}>
        <StatCard
          label="Total"
          value={m.total}
          accent="#444"
          active={false}
          onClick={() => onFilterChange(null)}
        />
        <StatCard label="Active"      value={m.byStatus.active}       accent={statusColors.active}       active={isActive('status','active')}              onClick={() => toggle('status','active')} />
        <StatCard label="New"         value={m.byStatus.new}          accent={statusColors.new}          active={isActive('status','new')}                 onClick={() => toggle('status','new')} />
        <StatCard label="Retired"     value={m.byStatus.retired}      accent={statusColors.retired}      active={isActive('status','retired')}             onClick={() => toggle('status','retired')} />
        <StatCard label="Unreviewed"  value={m.byReview.unreviewed}   accent={reviewColors.unreviewed}   active={isActive('reviewStatus','unreviewed')}    onClick={() => toggle('reviewStatus','unreviewed')} />
        <StatCard label="Reported"    value={m.byReview.reported}     accent={reviewColors.reported}     active={isActive('reviewStatus','reported')}      onClick={() => toggle('reviewStatus','reported')} />
        <StatCard label="In Review"   value={m.byReview.in_review}    accent={reviewColors.in_review}    active={isActive('reviewStatus','in_review')}     onClick={() => toggle('reviewStatus','in_review')} />
        <StatCard label="Reviewed"    value={m.byReview.reviewed}     accent={reviewColors.reviewed}     active={isActive('reviewStatus','reviewed')}      onClick={() => toggle('reviewStatus','reviewed')} />
      </div>
    </>
  );
}

function DataQualitySection({ q, total, examId, activeFilter, onFilterChange }) {
  const items = [
    { key: 'awaitingApproval',   icon: '⏳', count: q.awaitingApproval,   label: 'questions awaiting approval (status: New)',          warn: q.awaitingApproval > 0 },
    { key: 'missingExplanation', icon: '📝', count: q.missingExplanation, label: 'questions missing an explanation',                   warn: q.missingExplanation > 0 },
    { key: 'noCategory',         icon: '🏷️', count: q.noCategory,         label: 'questions with no category assigned',                warn: q.noCategory > 0 },
    { key: 'invalidCategory',    icon: '⚠️', count: q.invalidCategory,    label: 'questions with an unknown / legacy category value',  warn: q.invalidCategory > 0 },
  ];

  const invalidExtra =
    examId === 'all'
      ? { validCategoriesByExam: EXAM_CATEGORIES }
      : { validCategories: EXAM_CATEGORIES[examId] || [] };
  const FILTERS = {
    awaitingApproval:   { field: 'status', value: 'new' },
    missingExplanation: { field: 'missingExplanation', value: true },
    noCategory:         { field: 'category', value: '(uncategorized)' },
    invalidCategory:    { field: 'category', value: '(invalid)', ...invalidExtra },
  };
  const isActive = (f) => activeFilter?.field === f.field && activeFilter?.value === f.value;
  const toggle = (f) => { if (!onFilterChange) return; if (isActive(f)) onFilterChange(null); else onFilterChange(f); };

  return (
    <>
      <p style={S.sectionLabel}>
        Data Quality
        {onFilterChange && (
          <span style={{ marginLeft: '8px', fontWeight: '400', color: BRAND.textLight, textTransform: 'none', letterSpacing: 0, fontSize: '0.72rem' }}>
            — click a tile to filter
          </span>
        )}
      </p>
      <div style={S.alertGrid}>
        {items.map(({ key, icon, count, label, warn }) => {
          const f = FILTERS[key];
          const active = isActive(f);
          const clickable = !!onFilterChange;
          return (
          <div
            key={key}
            data-testid={`data-quality-${key}`}
            onClick={clickable ? () => toggle(f) : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(f); } } : undefined}
            title={clickable ? (active ? 'Click to clear filter' : 'Click to view these questions') : undefined}
            style={{
              ...S.alertCard(warn && count > 0),
              cursor: clickable ? 'pointer' : 'default',
              outline: active ? `2px solid ${BRAND.accent}` : 'none',
              outlineOffset: '-2px',
            }}
          >
            <span style={S.alertIcon}>{icon}</span>
            <div style={S.alertText}>
              <span style={S.alertCount(warn && count > 0)}>{count}</span>
              {' '}{label}
              {total > 0 && count > 0 && (
                <span style={{ color: BRAND.textLight }}> ({Math.round((count / total) * 100)}%)</span>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </>
  );
}

function BarRow({ label, count, total, color = BRAND.accent, onClick, active }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  const isClickable = typeof onClick === 'function';
  return (
    <div
      style={{
        ...S.barRow,
        cursor:       isClickable ? 'pointer' : 'default',
        borderRadius: '6px',
        padding:      active ? '4px 6px' : '0 6px',
        margin:       '0 -6px 8px',
        background:   active ? '#e8f0fe' : 'transparent',
        outline:      active ? `2px solid ${BRAND.accent}` : 'none',
        outlineOffset: '-2px',
        transition:   'background 0.1s, outline 0.1s',
      }}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      title={isClickable ? (active ? 'Click to clear filter' : `Filter by "${label}"`) : undefined}
    >
      <div style={S.barLabel} title={label}>{label}</div>
      <div style={S.barTrack}>
        <div style={S.barFill(pct, active ? BRAND.accent : color)} />
      </div>
      <div style={{ ...S.barCount, color: active ? BRAND.accent : BRAND.textMuted, fontWeight: active ? '700' : '400' }}>
        {count}
      </div>
    </div>
  );
}

function CategoryBreakdown({ m, examId, activeFilter, onFilterChange }) {
  const cats = EXAM_CATEGORIES[examId] || [];
  if (examId === 'all' || cats.length === 0) {
    return (
      <div style={S.card}>
        <p style={{ ...S.sectionLabel, marginBottom: '8px' }}>Category Breakdown</p>
        <p style={{ fontSize: '0.82rem', color: BRAND.textLight, marginTop: 0 }}>
          Select a specific exam tab to see the category breakdown.
        </p>
      </div>
    );
  }

  function toggleCategory(value, extra = {}) {
    if (!onFilterChange) return;
    if (activeFilter?.field === 'category' && activeFilter?.value === value) {
      onFilterChange(null);
    } else {
      onFilterChange({ field: 'category', value, ...extra });
    }
  }

  const catColors = ['#1565c0','#2e7d32','#6a1b9a','#e07b00','#c62828','#00838f'];
  const rows = [
    ...cats.map((c, i) => ({
      label: c,
      count: m.byCategory[c] || 0,
      color: catColors[i % catColors.length],
      filterExtra: {},
    })),
    { label: '(uncategorized)', count: m.byCategory['(uncategorized)'] || 0, color: '#bbb',     filterExtra: {} },
    { label: '(invalid)',       count: m.byCategory['(invalid)']       || 0, color: '#ef9a9a', filterExtra: { validCategories: cats } },
  ].filter((r) => r.count > 0 || cats.includes(r.label));

  return (
    <div style={S.card}>
      <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>
        {examId === 'pmp' ? 'Phase Breakdown' : 'Category Breakdown'}
        {onFilterChange && (
          <span style={{ marginLeft: '8px', fontWeight: '400', color: BRAND.textLight, textTransform: 'none', letterSpacing: 0, fontSize: '0.72rem' }}>
            — click to filter
          </span>
        )}
      </p>
      {rows.map((r) => (
        <BarRow
          key={r.label}
          label={r.label}
          count={r.count}
          total={m.total}
          color={r.color}
          onClick={onFilterChange ? () => toggleCategory(r.label, r.filterExtra) : undefined}
          active={activeFilter?.field === 'category' && activeFilter?.value === r.label}
        />
      ))}
    </div>
  );
}

/**
 * PMP-only breakdown of questions by performanceDomain and deliveryContext.
 * Renders two cards side by side. Both are optional fields; untagged
 * questions fall into '(unassigned)'. Only rendered when selectedExam === 'pmp'.
 */
function PmpDimensionsBreakdown({ m, activeFilter, onFilterChange }) {
  const domainColors  = ['#1565c0', '#2e7d32', '#e07b00'];
  const contextColors = ['#6a1b9a', '#00838f', '#c62828', '#e07b00'];

  const domainRows = [
    ...PMP_PERFORMANCE_DOMAINS.map((d, i) => ({
      label: d,
      count: m.byPerformanceDomain?.[d] || 0,
      color: domainColors[i % domainColors.length],
    })),
    { label: '(unassigned)', count: m.byPerformanceDomain?.['(unassigned)'] || 0, color: '#bbb' },
  ];

  const contextRows = [
    ...PMP_DELIVERY_CONTEXTS.map((c, i) => ({
      label: c,
      count: m.byDeliveryContext?.[c] || 0,
      color: contextColors[i % contextColors.length],
    })),
    { label: '(unassigned)', count: m.byDeliveryContext?.['(unassigned)'] || 0, color: '#bbb' },
  ];

  function toggle(field, value, validValues) {
    if (!onFilterChange) return;
    if (activeFilter?.field === field && activeFilter?.value === value) onFilterChange(null);
    else onFilterChange({ field, value, validValues });
  }

  const suffix = (extra) => (
    <span style={{ marginLeft: '8px', fontWeight: '400', color: BRAND.textLight, textTransform: 'none', letterSpacing: 0, fontSize: '0.72rem' }}>
      {extra}
    </span>
  );
  const onlyHint = onFilterChange ? '— PMP only · click to filter' : '— PMP only';

  return (
    <div style={S.twoCol} data-testid="pmp-dimensions-breakdown">
      <div style={S.card}>
        <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>
          Performance Domain
          {suffix(onlyHint)}
        </p>
        {domainRows.map((r) => (
          <BarRow
            key={r.label}
            label={r.label}
            count={r.count}
            total={m.total}
            color={r.color}
            onClick={onFilterChange ? () => toggle('performanceDomain', r.label, PMP_PERFORMANCE_DOMAINS) : undefined}
            active={activeFilter?.field === 'performanceDomain' && activeFilter?.value === r.label}
          />
        ))}
      </div>
      <div style={S.card}>
        <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>
          Delivery Context
          {suffix(onlyHint)}
        </p>
        {contextRows.map((r) => (
          <BarRow
            key={r.label}
            label={r.label}
            count={r.count}
            total={m.total}
            color={r.color}
            onClick={onFilterChange ? () => toggle('deliveryContext', r.label, PMP_DELIVERY_CONTEXTS) : undefined}
            active={activeFilter?.field === 'deliveryContext' && activeFilter?.value === r.label}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Config-driven admin distribution for a cert's SECONDARY metadata dimension
 * (e.g. PMI-PMOCP Focus Areas). Receives the precomputed `dist` from
 * dashboardService.buildExamMetrics (buildSecondaryDistribution) — which is
 * null for certs without a secondary dimension, so this component is never
 * mounted for pmp / cpmai / pmi-acp.
 *
 * Surfaces: per-value counts, missing, invalid (not in vocab + not
 * Unclassified), and flags low-count values (< dist.lowCountThreshold).
 */
function SecondaryDimensionBreakdown({ dist, total, activeFilter, onFilterChange }) {
  const palette = ['#1565c0','#2e7d32','#6a1b9a','#e07b00','#c62828','#00838f','#5e35b1','#00897b','#d81b60','#3949ab'];
  const lowSet = new Set(dist.lowCount || []);
  const barLabels = Object.keys(dist.byArea || {});
  const valueRows = barLabels
    .filter((label) => label !== dist.labelSingular) // defensive; not expected
    .map((label, i) => ({
      label,
      count: dist.byArea[label] || 0,
      color: palette[i % palette.length],
      low: lowSet.has(label),
    }));

  const isActive = (value) =>
    activeFilter?.field === 'focusArea' && activeFilter?.value === value;

  function toggle(value) {
    if (!onFilterChange) return;
    if (isActive(value)) {
      onFilterChange(null);
    } else {
      onFilterChange({
        field: 'focusArea',
        value,
        dimensionKey: dist.key,
        dimensionLabel: dist.labelSingular,
        barLabels,
      });
    }
  }

  const statButton = (value, node) =>
    onFilterChange ? (
      <button
        onClick={() => toggle(value)}
        style={{
          background: isActive(value) ? '#e8f0fe' : 'none',
          border: 'none', padding: '2px 4px', margin: '-2px -4px',
          borderRadius: '4px', cursor: 'pointer', font: 'inherit', color: 'inherit',
          outline: isActive(value) ? `2px solid ${BRAND.accent}` : 'none',
        }}
        title="Click to view these questions"
      >
        {node}
      </button>
    ) : node;

  return (
    <div style={S.card} data-testid="secondary-dimension-breakdown">
      <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>
        {dist.label} Distribution
        <span style={{ marginLeft: '8px', fontWeight: '400', color: BRAND.textLight, textTransform: 'none', letterSpacing: 0, fontSize: '0.72rem' }}>
          {onFilterChange ? '— secondary dimension · click to filter' : '— secondary dimension'}
        </span>
      </p>

      {valueRows.map((r) => (
        <div key={r.label} data-testid={`focus-area-row-${r.label}`}>
          <BarRow
            label={r.low ? `${r.label}  ⚠ low` : r.label}
            count={r.count}
            total={total}
            color={r.low ? '#e9a23b' : r.color}
            onClick={onFilterChange ? () => toggle(r.label) : undefined}
            active={isActive(r.label)}
          />
        </div>
      ))}

      <div style={{ marginTop: '12px', display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '0.78rem', color: BRAND.textMuted }}>
        <span data-testid="focus-area-missing">Missing: {statButton('(missing)', <b style={{ color: dist.missing > 0 ? '#c62828' : BRAND.text }}>{dist.missing}</b>)}</span>
        <span data-testid="focus-area-invalid">Invalid: {statButton('(invalid)', <b style={{ color: dist.invalid > 0 ? '#c62828' : BRAND.text }}>{dist.invalid}</b>)}</span>
        <span data-testid="focus-area-unclassified">Unclassified: <b>{dist.unclassified}</b></span>
        {(dist.lowCount || []).length > 0 && (
          <span data-testid="focus-area-lowcount" style={{ color: '#e07b00' }}>
            ⚠ {dist.lowCount.length} below {dist.lowCountThreshold} questions
          </span>
        )}
      </div>
    </div>
  );
}

function TypeBreakdown({ m, activeFilter, onFilterChange }) {
  const knownTypes = QUESTION_TYPES.map((t) => t.key);
  const rows = QUESTION_TYPES.map((t) => ({ label: t.label, value: t.key, count: m.byType[t.key] || 0, color: '#1565c0' }));
  if (m.byType.unknown > 0) rows.push({ label: 'Other / Unknown', value: 'unknown', count: m.byType.unknown, color: '#bbb' });

  function toggle(value) {
    if (!onFilterChange) return;
    if (activeFilter?.field === 'type' && activeFilter?.value === value) onFilterChange(null);
    else onFilterChange({ field: 'type', value, knownTypes });
  }

  return (
    <div style={S.card}>
      <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>
        Question Types
        {onFilterChange && (
          <span style={{ marginLeft: '8px', fontWeight: '400', color: BRAND.textLight, textTransform: 'none', letterSpacing: 0, fontSize: '0.72rem' }}>
            — click to filter
          </span>
        )}
      </p>
      {rows.map((r) => (
        <BarRow
          key={r.value}
          label={r.label}
          count={r.count}
          total={m.total}
          color={r.color}
          onClick={onFilterChange ? () => toggle(r.value) : undefined}
          active={activeFilter?.field === 'type' && activeFilter?.value === r.value}
        />
      ))}
    </div>
  );
}

function ReviewWorkflowSection({ m }) {
  const rows = [
    { key: 'unreviewed', label: REVIEW_STATUS_LABELS.unreviewed, count: m.byReview.unreviewed },
    { key: 'reported',   label: REVIEW_STATUS_LABELS.reported,   count: m.byReview.reported },
    { key: 'in_review',  label: REVIEW_STATUS_LABELS.in_review,  count: m.byReview.in_review },
    { key: 'reviewed',   label: REVIEW_STATUS_LABELS.reviewed,   count: m.byReview.reviewed },
  ];
  return (
    <div style={S.card}>
      <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>Review Workflow</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${BRAND.border}` }}>
            <th style={{ textAlign: 'left', padding: '6px 4px', color: BRAND.textMuted, fontWeight: '600' }}>Review Status</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', color: BRAND.textMuted, fontWeight: '600' }}>Count</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', color: BRAND.textMuted, fontWeight: '600' }}>% of Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, label, count }) => (
            <tr key={key} style={{ borderBottom: '1px solid #f5f5f5' }}>
              <td style={{ padding: '8px 4px' }}>
                <span style={S.badge(REVIEW_STATUS_COLORS[key])}>{label}</span>
              </td>
              <td style={{ textAlign: 'right', padding: '8px 4px', fontWeight: '600' }}>{count}</td>
              <td style={{ textAlign: 'right', padding: '8px 4px', color: BRAND.textMuted }}>
                {m.total > 0 ? `${Math.round((count / m.total) * 100)}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Session metrics helpers ───────────────────────────────────────────────────

function pct(value) {
  return value === null || value === undefined ? '—' : `${value}%`;
}

function numOrDash(value) {
  return value === null || value === undefined ? '—' : value;
}

/** Renders a duration in seconds as a "Xh Ym" / "Xm" / "Xs" string. */
function formatDuration(totalSeconds) {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '—';
  const hours = Math.floor(totalSeconds / 3600);
  const mins  = Math.floor((totalSeconds % 3600) / 60);
  const secs  = totalSeconds % 60;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  if (mins  > 0) return `${mins}m`;
  return `${secs}s`;
}

/**
 * Renders one row group of metrics (label/value pairs) as a table column for
 * each visible exam. Used by the Full Exam, Practice, and Overall sections so
 * styling stays consistent with the rest of the dashboard.
 *
 * `rows` items: { label, getValue(metrics) → string|number, color?, bold? }
 */
function MetricsTable({ visibleExams, rows, getMetricsForExam, emptyMessage, hasAnyData }) {
  if (!hasAnyData) {
    return <div style={S.emptyRow}>{emptyMessage}</div>;
  }
  return (
    <table style={S.sessionTable}>
      <thead>
        <tr>
          <th style={S.sessionTh}>Metric</th>
          {visibleExams.map((e) => (
            <th key={e.examId} style={S.sessionThRight}>{e.displayName}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            <td style={{
              ...S.sessionTd,
              color: row.color || BRAND.text,
              fontWeight: row.bold ? '600' : '400',
            }}>
              {row.label}
            </td>
            {visibleExams.map((e) => {
              const m = getMetricsForExam(e.examId);
              const val = row.getValue(m);
              return (
                <td key={e.examId} style={{
                  ...S.sessionTdRight,
                  color: row.color || BRAND.text,
                  fontWeight: row.bold ? '700' : '600',
                }}>
                  {val}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Full Exam Performance — exam readiness signal.
 *
 * Pass/fail and score averages live ONLY here; they are computed exclusively
 * from full-exam sessions so practice activity can never inflate or dilute
 * the readiness picture.
 */
function FullExamSection({ sessionMetrics, allExams, selectedExam }) {
  const visibleExams = selectedExam === 'all'
    ? allExams
    : allExams.filter((e) => e.examId === selectedExam);

  const getFull = (examId) => sessionMetrics?.[examId]?.full;

  const passRatePct = (full) => {
    if (!full) return null;
    const decided = (full.passCount || 0) + (full.failCount || 0);
    if (decided === 0) return null;
    return Math.round((full.passCount / decided) * 100);
  };

  const rows = [
    {
      label: 'Exams Taken',
      getValue: (full) => numOrDash(full?.count),
      bold: true,
    },
    {
      label: `Pass Rate  (score ≥ ${PASS_THRESHOLD}%)`,
      getValue: (full) => pct(passRatePct(full)),
      color: '#2e7d32',
    },
    {
      label: 'Average Score',
      getValue: (full) => pct(full?.avgScorePct),
    },
    {
      label: 'Average Completion',
      getValue: (full) => pct(full?.avgCompletionPct),
    },
    {
      label: 'Early Exits',
      getValue: (full) => numOrDash(full?.earlyExitCount),
      color: '#777',
    },
    {
      label: 'Best Score',
      getValue: (full) => pct(full?.bestScorePct),
    },
    {
      label: 'Most Recent Score',
      getValue: (full) => pct(full?.mostRecentScorePct),
    },
  ];

  const hasAnyData = visibleExams.some(
    (e) => (getFull(e.examId)?.count || 0) > 0
  );

  return (
    <div style={S.sessionCard} data-testid="full-exam-section">
      <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>
        Full Exam Performance
        <span style={{ marginLeft: '8px', fontWeight: '400', color: BRAND.textLight, textTransform: 'none', letterSpacing: 0, fontSize: '0.72rem' }}>
          — exam readiness, full-length sessions only
        </span>
      </p>
      <MetricsTable
        visibleExams={visibleExams}
        rows={rows}
        getMetricsForExam={getFull}
        hasAnyData={hasAnyData}
        emptyMessage="No full-length exam sessions recorded yet."
      />
    </div>
  );
}

/**
 * Practice Performance — learning-activity signal.
 *
 * Deliberately omits any pass/fail framing.  Practice scores reflect study
 * progress, not readiness, and conflating the two is what this refactor is
 * stamping out.
 */
function PracticeSection({ sessionMetrics, allExams, selectedExam }) {
  const visibleExams = selectedExam === 'all'
    ? allExams
    : allExams.filter((e) => e.examId === selectedExam);

  const getPractice = (examId) => sessionMetrics?.[examId]?.practice;

  const rows = [
    {
      label: 'Sessions Taken',
      getValue: (p) => numOrDash(p?.count),
      bold: true,
    },
    {
      label: 'Average Score',
      getValue: (p) => pct(p?.avgScorePct),
    },
    {
      label: 'Average Completion',
      getValue: (p) => pct(p?.avgCompletionPct),
    },
    {
      label: 'Early Exits',
      getValue: (p) => numOrDash(p?.earlyExitCount),
      color: '#777',
    },
    {
      label: 'Questions Answered',
      getValue: (p) => numOrDash(p?.questionsAnswered),
    },
  ];

  const hasAnyData = visibleExams.some(
    (e) => (getPractice(e.examId)?.count || 0) > 0
  );

  return (
    <div style={S.sessionCard} data-testid="practice-section">
      <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>
        Practice Performance
        <span style={{ marginLeft: '8px', fontWeight: '400', color: BRAND.textLight, textTransform: 'none', letterSpacing: 0, fontSize: '0.72rem' }}>
          — learning activity, practice sessions only
        </span>
      </p>
      <MetricsTable
        visibleExams={visibleExams}
        rows={rows}
        getMetricsForExam={getPractice}
        hasAnyData={hasAnyData}
        emptyMessage="No practice sessions recorded yet."
      />
    </div>
  );
}

/**
 * Overall Activity — neutral totals only (counts and time spent).
 *
 * Intentionally avoids any score / pass / completion averages; those would
 * mix full and practice modes, which is exactly what the dashboard refactor
 * eliminates.
 */
function OverallActivitySection({ sessionMetrics, allExams, selectedExam }) {
  const visibleExams = selectedExam === 'all'
    ? allExams
    : allExams.filter((e) => e.examId === selectedExam);

  const get = (examId) => sessionMetrics?.[examId];

  const totalSessions = (m) =>
    (m?.fullCount || 0) + (m?.practiceCount || 0);

  const rows = [
    {
      label: 'Total Sessions',
      getValue: (m) => numOrDash(totalSessions(m)),
      bold: true,
    },
    {
      label: 'Total Questions Answered',
      getValue: (m) => numOrDash(m?.totalQuestionsAnswered),
    },
    {
      label: 'Time Spent',
      getValue: (m) => formatDuration(m?.totalDurationSeconds || 0),
    },
  ];

  const hasAnyData = visibleExams.some((e) => totalSessions(get(e.examId)) > 0);

  return (
    <div style={S.sessionCard} data-testid="overall-activity-section">
      <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>
        Overall Activity
        <span style={{ marginLeft: '8px', fontWeight: '400', color: BRAND.textLight, textTransform: 'none', letterSpacing: 0, fontSize: '0.72rem' }}>
          — totals across all sessions
        </span>
      </p>
      <MetricsTable
        visibleExams={visibleExams}
        rows={rows}
        getMetricsForExam={get}
        hasAnyData={hasAnyData}
        emptyMessage="No exam sessions recorded yet."
      />
    </div>
  );
}

function RecentActivitySection({ activity, selectedExamId }) {
  const filtered = selectedExamId === 'all'
    ? activity
    : activity.filter((a) => a.examId === selectedExamId);

  const typeSummary = {};
  for (const a of filtered) typeSummary[a.changeType] = (typeSummary[a.changeType] || 0) + 1;

  return (
    <div style={S.activityCard}>
      <p style={{ ...S.sectionLabel, marginBottom: '14px' }}>
        Recent Activity
        {filtered.length > 0 && (
          <span style={{ marginLeft: '8px', fontWeight: '400', color: BRAND.textLight, textTransform: 'none', letterSpacing: 0, fontSize: '0.75rem' }}>
            ({filtered.length} events)
          </span>
        )}
      </p>
      {Object.keys(typeSummary).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
          {Object.entries(typeSummary).map(([type, count]) => {
            const meta = CHANGE_TYPE_META[type] || { label: type, icon: '•', color: '#555' };
            return (
              <span key={type} style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', background: '#f0f0f0', color: meta.color, border: '1px solid #e0e0e0' }}>
                {meta.icon} {meta.label} ({count})
              </span>
            );
          })}
        </div>
      )}
      {filtered.length === 0 ? (
        <div style={S.emptyRow}>No recent activity found.</div>
      ) : (
        <ul style={S.activityList}>
          {filtered.slice(0, 30).map((a) => {
            const meta = CHANGE_TYPE_META[a.changeType] || { label: a.changeType, icon: '•', color: '#555' };
            return (
              <li key={a.id} style={S.activityItem}>
                <span style={{ ...S.activityIcon, color: meta.color }}>{meta.icon}</span>
                <div style={S.activityBody}>
                  <span style={{ fontWeight: '600', color: meta.color }}>{meta.label}</span>{' '}
                  <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: BRAND.textMuted }}>{a.questionId}</span>
                  {selectedExamId === 'all' && (
                    <span style={{ marginLeft: '6px', fontSize: '0.72rem', background: '#f0f0f0', padding: '1px 6px', borderRadius: '8px', color: BRAND.textMuted }}>
                      {(a.examId || '?').toUpperCase()}
                    </span>
                  )}
                  {a.changeNote && <div style={S.activityMeta}>"{a.changeNote}"</div>}
                </div>
                <div style={S.activityTime} title={formatTimestamp(a.changedAt)}>
                  {formatRelativeTime(a.changedAt)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Cross-cert "Domain coverage heatmap" — question-bank health at a glance.
 * For each active cert it shows active (published) inventory per domain vs a
 * blueprint-weighted target, coloured by depth %. Rendered on the All tab.
 * Data + targets come from coverageService (driven by getActiveCertIds), so
 * new certs appear automatically.
 */
function DomainCoverageHeatmap({ questionsByExam, throughput = {} }) {
  const rows = computeCoverageMatrix(questionsByExam);
  if (!rows.length) return null;

  const cols = maxDomainColumns(rows) || 1;
  const LABEL_W = '108px';
  const SUMMARY_W = '150px';

  const cellStyle = (c) => ({
    minWidth: 0,
    height: '30px',
    borderRadius: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#fff',
    background: c.color,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  });

  return (
    <div style={S.card} data-testid="domain-coverage-heatmap">
      <p style={{ ...S.sectionLabel, marginBottom: '4px' }}>Domain Coverage Heatmap</p>
      <p style={{ fontSize: '0.78rem', color: BRAND.textLight, marginTop: 0, marginBottom: '14px' }}>
        Active inventory vs blueprint-weighted target — 100% = fully stocked. Fill-up ETA from recent Question Factory output.
      </p>

      {rows.map((row) => {
        const rate = throughput[row.examId] && throughput[row.examId].perDay;
        const eta = formatEta(row.gapTotal, rate);
        return (
          <div
            key={row.examId}
            style={{ display: 'grid', gridTemplateColumns: `${LABEL_W} 1fr ${SUMMARY_W}`, alignItems: 'center', gap: '12px', marginBottom: '8px' }}
          >
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: BRAND.text }}>{row.displayName}</div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '6px' }}>
              {row.cells.map((c) => (
                <div
                  key={c.category}
                  title={`${c.category}: ${c.active} active / ${c.target} target (${c.depthPct}%)${c.gap > 0 ? ` — ${c.gap} to go` : ''}`}
                  style={cellStyle(c)}
                >
                  {c.label} {c.depthPct}
                </div>
              ))}
            </div>
            <div style={{ fontSize: '0.72rem', textAlign: 'right', lineHeight: 1.3 }}>
              {row.gapTotal > 0 ? (
                <>
                  <span style={{ fontWeight: 700, color: BRAND.text }}>+{row.gapTotal} to target</span>
                  <br />
                  <span style={{ color: eta.etaDays === null ? '#c62828' : BRAND.textMuted }}>{eta.text}</span>
                </>
              ) : (
                <span style={{ fontWeight: 700, color: '#2e7d32' }}>✓ Fully stocked</span>
              )}
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '0.72rem', color: BRAND.textMuted, marginTop: '10px', paddingLeft: `calc(${LABEL_W} + 12px)` }}>
        {COVERAGE_BANDS.map((b) => (
          <span key={b.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ width: '11px', height: '11px', borderRadius: '3px', background: b.color, display: 'inline-block' }} />
            {b.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard({ onBack, onEditQuestion, initialExam = 'all' }) {
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [data,          setData]          = useState(null);
  const [selectedExam,  setSelectedExam]  = useState(initialExam);
  const [activeFilter,  setActiveFilter]  = useState(null); // { field, value } | null
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [throughput,    setThroughput]    = useState({});

  const filterPanelRef = useRef(null);
  const allExams = getAllExams();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setActiveFilter(null);
    try {
      const [result, tput] = await Promise.all([
        loadDashboardData(),
        getPublishThroughput().catch(() => ({})),
      ]);
      setData(result);
      setThroughput(tput || {});
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('[AdminDashboard] Load failed:', err);
      setError(err.message || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Scroll the filter panel into view whenever a filter is activated
  useEffect(() => {
    if (activeFilter && filterPanelRef.current) {
      setTimeout(() => filterPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  }, [activeFilter]);

  // Clear filter when changing exam tab
  function handleTabChange(examId) {
    setSelectedExam(examId);
    setActiveFilter(null);
  }

  const activeMetrics = data
    ? (selectedExam === 'all' ? data.overall : data.metrics[selectedExam])
    : null;

  // Questions visible in the current tab (for the filter list)
  const tabQuestions = data ? questionsForTab(data.questions, selectedExam) : [];

  return (
    <div data-testid="admin-dashboard" style={S.wrapper}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={S.header}>
        <button data-testid="dashboard-back-button" style={S.backButton} onClick={onBack}>
          ← Back
        </button>
        <span style={S.headerTitle}>📊 Question Bank Dashboard</span>
        <div style={S.headerMeta}>
          {lastRefreshed && <span>Updated {formatRelativeTime(lastRefreshed.toISOString())}</span>}
          <button style={S.refreshButton} onClick={loadData} disabled={loading}>
            {loading ? '⟳ Loading…' : '⟳ Refresh'}
          </button>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div style={S.body}>

        {/* Exam filter tabs */}
        <div style={S.tabBar}>
          {[{ examId: 'all', displayName: 'All Exams' }, ...allExams].map((exam) => (
            <button
              key={exam.examId}
              data-testid={`dashboard-tab-${exam.examId}`}
              style={S.tab(selectedExam === exam.examId)}
              onClick={() => handleTabChange(exam.examId)}
            >
              {exam.displayName}
            </button>
          ))}
        </div>

        {error && <div style={S.errorBox}>⚠️ {error}</div>}
        {loading && !data && <div style={S.center}>Loading dashboard data…</div>}

        {!loading && activeMetrics && (
          <>
            {/* Summary stat cards — clickable */}
            <SummaryCards
              m={activeMetrics}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
            />

            {/* Filtered question list — appears below cards when a filter is active */}
            <FilteredQuestionList
              filter={activeFilter}
              questions={tabQuestions}
              selectedExam={selectedExam}
              filterPanelRef={filterPanelRef}
              onEditQuestion={onEditQuestion}
            />

            {/* Domain coverage heatmap — cross-cert bank health (All tab) */}
            {selectedExam === 'all' && (
              <DomainCoverageHeatmap questionsByExam={data.questions} throughput={throughput} />
            )}

            {/* Data quality — clickable tiles */}
            <DataQualitySection
              q={activeMetrics.quality}
              total={activeMetrics.total}
              examId={selectedExam}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
            />

            {/* Two-column: categories + types */}
            <div style={S.twoCol}>
              <CategoryBreakdown
                m={activeMetrics}
                examId={selectedExam}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
              />
              <TypeBreakdown
                m={activeMetrics}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
              />
            </div>

            {/* PMP-only taxonomy dimensions (performanceDomain + deliveryContext) */}
            {selectedExam === 'pmp' && (
              <PmpDimensionsBreakdown
                m={activeMetrics}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
              />
            )}

            {/* Secondary-dimension distribution (config-driven; rendered only
                when the selected cert declares one — activeMetrics.byFocusArea
                is null otherwise). */}
            {activeMetrics.byFocusArea && (
              <SecondaryDimensionBreakdown
                dist={activeMetrics.byFocusArea}
                total={activeMetrics.total}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
              />
            )}

            {/* Review workflow */}
            <div style={{ marginBottom: '28px' }}>
              <ReviewWorkflowSection m={activeMetrics} />
            </div>

            {/* Full Exam Performance — exam readiness, no practice mixed in */}
            <FullExamSection
              sessionMetrics={data.sessionMetrics}
              allExams={allExams}
              selectedExam={selectedExam}
            />

            {/* Practice Performance — learning activity, no pass/fail framing */}
            <PracticeSection
              sessionMetrics={data.sessionMetrics}
              allExams={allExams}
              selectedExam={selectedExam}
            />

            {/* Overall Activity — neutral totals (sessions, questions, time) */}
            <OverallActivitySection
              sessionMetrics={data.sessionMetrics}
              allExams={allExams}
              selectedExam={selectedExam}
            />

            {/* Recent activity */}
            <RecentActivitySection activity={data.recentActivity} selectedExamId={selectedExam} />
          </>
        )}

      </div>
    </div>
  );
}
